'use client';

import { createBrowserClient } from '@supabase/ssr';
import { env } from '@/lib/env';
import { denamespaceCookies, isNamespaced, namespaceCookieName } from './cookie-namespace';

let client: ReturnType<typeof createBrowserClient> | null = null;

/**
 * Parse all cookies from document.cookie string.
 * Returns array of { name, value } objects.
 * URI-decodes names and values to match the encoding applied by setBrowserCookie.
 */
function parseBrowserCookies(): Array<{ name: string; value: string }> {
  if (typeof document === 'undefined') return [];
  return document.cookie.split(';').reduce<Array<{ name: string; value: string }>>((acc, raw) => {
    const idx = raw.indexOf('=');
    if (idx === -1) return acc;
    try {
      const name = decodeURIComponent(raw.slice(0, idx).trim());
      const value = decodeURIComponent(raw.slice(idx + 1).trim());
      if (name) acc.push({ name, value });
    } catch {
      // Skip malformed/non-encoded cookies
    }
    return acc;
  }, []);
}

type BrowserCookieOptions = {
  path?: string;
  maxAge?: number;
  domain?: string;
  expires?: Date;
  // `boolean` comes from cookie@1 SerializeOptions: `false` means no sameSite attribute.
  sameSite?: boolean | 'lax' | 'strict' | 'none' | 'Lax' | 'Strict' | 'None';
  secure?: boolean;
  partitioned?: boolean;
};

/**
 * Set a cookie in the browser via document.cookie.
 * Preserves the full set of client-applicable cookie options provided by
 * Supabase SSR. `httpOnly` is intentionally omitted because it cannot be set
 * from client-side JavaScript.
 */
function setBrowserCookie(name: string, value: string, options?: BrowserCookieOptions) {
  if (typeof document === 'undefined') return;

  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options?.path) cookie += `; path=${options.path}`;
  if (options?.maxAge !== undefined) cookie += `; max-age=${options.maxAge}`;
  if (options?.domain) cookie += `; domain=${options.domain}`;
  if (options?.expires) cookie += `; expires=${options.expires.toUTCString()}`;
  // sameSite: false means "no SameSite attribute" (cookie package convention) — skip it.
  // sameSite: true is a legacy alias for 'strict'. Capitalize for RFC 6265bis conformance.
  if (options?.sameSite != null && options.sameSite !== false) {
    const raw = options.sameSite === true ? 'strict' : options.sameSite;
    const sameSiteValue = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    cookie += `; SameSite=${sameSiteValue}`;
  }
  if (options?.secure) cookie += '; secure';
  if (options?.partitioned) cookie += '; partitioned';

  document.cookie = cookie;
}

export function getSupabaseBrowserClient() {
  if (client) return client;

  // When no namespace is active (production), use the default behavior
  // for zero overhead and full backward compatibility.
  if (!isNamespaced) {
    client = createBrowserClient(env.NEXT_PUBLIC_SUPABASE_URL, env.NEXT_PUBLIC_SUPABASE_ANON_KEY);
    return client;
  }

  // In namespaced deployments (staging, preview, localhost), intercept
  // cookie read/write to add the deployment suffix.
  client = createBrowserClient(env.NEXT_PUBLIC_SUPABASE_URL, env.NEXT_PUBLIC_SUPABASE_ANON_KEY, {
    cookies: {
      getAll() {
        // Use the shared helper: sb-* only + dedup prefers suffixed variants.
        // Non-sb cookies pass through untouched; coexisting unsuffixed+suffixed
        // sb-* variants resolve to the deployment-specific (suffixed) one.
        return denamespaceCookies(parseBrowserCookies());
      },
      setAll(cookiesToSet) {
        cookiesToSet.forEach(({ name, value, options }) => {
          setBrowserCookie(namespaceCookieName(name), value, options);
        });
      },
    },
  });

  return client;
}
